import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { useCsvImport } from '@/hooks/useCsvImport';
import { type ParsedProspectData, type ImportError } from '@/pages/Import';

interface CsvUploadProps {
  onFileProcessed: (data: ParsedProspectData[], errors: ImportError[]) => void;
}

export function CsvUpload({ onFileProcessed }: CsvUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { parseCSV } = useCsvImport();

  const handleFileSelect = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleProcessFile = async () => {
    if (!file) return;

    setIsProcessing(true);
    try {
      const { data, errors } = await parseCSV(file);
      onFileProcessed(data, errors);
    } catch (error) {
      // Log error details in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Error processing CSV:', error);
      }
      alert('Error processing CSV file. Please check the format and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center space-y-4">
          <Upload className="h-12 w-12 text-gray-400" />
          <div>
            <p className="text-lg font-medium">Drop your CSV file here</p>
            <p className="text-sm text-gray-500">or click to browse</p>
          </div>
          <Button variant="outline" onClick={handleBrowseClick} disabled={isProcessing}>
            Browse Files
          </Button>
          <Input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Selected File Display */}
      {file && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                onClick={handleProcessFile}
                disabled={isProcessing}
                className="min-w-[120px]"
              >
                {isProcessing ? 'Processing...' : 'Process File'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CSV Format Instructions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h3 className="font-medium mb-2">CSV Format Requirements</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Required columns:</strong> company, contactName, email</p>
                <p><strong>Optional columns:</strong> phone, stage, notes</p>
                <p><strong>Stage values:</strong> new, contacted, qualified, interested, committed, participant</p>
                <p><strong>Example header row:</strong></p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                  company,contactName,email,phone,stage,notes
                </code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}